import { GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import * as React from 'react';
import {
    FacebookIcon,
    FacebookShareButton,
    TelegramIcon,
    TelegramShareButton,
    TwitterIcon,
    TwitterShareButton,
    WhatsappIcon,
    WhatsappShareButton
} from 'react-share';

import { Categories } from '../../components/Categories';
import { Iframe } from '../../components/Iframe/lazy';
import { Newsletter } from '../../components/Newsletter/lazy';
import { VerticalPost } from '../../components/Post/lazy';
import { md2Html } from '../../lib/md2Html';
import { getAllPostIds, getPostData, getRelatedPosts } from '../../lib/posts';
import { PostEntity } from '../../types';
import { titleBuilder } from '../../utils/titleBuilder';

const sharePost = [
    {
        key: 'whatsapp',
        Button: WhatsappShareButton,
        Icon: WhatsappIcon
    },
    {
        key: 'twitter',
        Button: TwitterShareButton,
        Icon: TwitterIcon
    },
    {
        key: 'facebook',
        Button: FacebookShareButton,
        Icon: FacebookIcon
    },
    {
        key: 'telegram',
        Button: TelegramShareButton,
        Icon: TelegramIcon
    }
];

const Page = ({
    post,
    relatedPosts
}: {
    post: PostEntity;
    relatedPosts: PostEntity[];
}): JSX.Element => {
    const router = useRouter();
    const pageUrl =
        (process.env.NODE_ENV === 'production'
            ? 'https://devsbrasil.com'
            : 'http://localhost:3000') + router.asPath;
    const [year, month, date] = post.date.split('-');
    const formattedDate = `${date}/${month}/${year}`;

    return (
        <>
            <Head>
                <title>{titleBuilder(post.title)}</title>
            </Head>
            <NextSeo
                openGraph={{
                    type: 'website',
                    url: pageUrl,
                    title: post.title,
                    description:
                        post.summary.replace(/<[^>]*>?/gm, '') ||
                        'Javascript, PHP, Rust, Visualização de Dados e muito mais! Aprenda ou aprimore seus conhecimentos com Video tutoriais e posts sobre programação',
                    images: [
                        {
                            url: `https://devsbrasil.com/images/maxresdefault/${post.source}.jpg`,
                            width: 1280,
                            height: 720,
                            alt: titleBuilder(post.title)
                        }
                    ]
                }}
            />
            <div className={'page-main single-post'}>
                <section className={'page-container max-w-screen-md'}>
                    <div className={'flex flex-col mb-12'}>
                        <Iframe
                            title={post.title}
                            showShadow
                            priority
                            src={post.source}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                        <div className={'flex flex-col'}>
                            <div className={'flex justify-between items-center'}>
                                <Categories categories={post.categories} />
                                <span className={'text-gray-main'}>{formattedDate}</span>
                            </div>
                            <h1 className={'page-title'}>{post.title}</h1>
                            <div
                                className={'text-lg text-gray-main mt-4 hidden lg:block leading-6'}
                                dangerouslySetInnerHTML={{ __html: post.summary }}
                            />
                        </div>
                        <div
                            className={
                                'border-gray-light border-t mt-8 flex align-center uppercase text-gray-main relative border-dashed border'
                            }>
                            <div className={'absolute -top-3 top-0 w-full text-center'}>
                                <span
                                    className={'bg-white px-2 font-medium tracking-widest text-sm'}>
                                    Compartilhe
                                </span>
                                <div className={'flex justify-center mt-1'}>
                                    {sharePost.map(({ key, Button, Icon }) => (
                                        <Button key={key} className={'mx-1'} url={pageUrl}>
                                            <Icon round size={28} />
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className={'mt-16'}>
                            <div
                                className={
                                    'page-content text-xl text-gray-dark mt-4 block leading-6'
                                }
                                dangerouslySetInnerHTML={{ __html: post.content || post.summary }}
                            />
                        </div>
                    </div>
                </section>
                <div className={'newsletter-wrapper mb-16 flex'}>
                    <Newsletter />
                </div>
                <div className={'mx-auto mb-16 w-11/12 max-w-screen-lg'}>
                    <h3 className={'text-gray-main text-lg font-bold mb-4'}>Veja também</h3>
                    <ul className={'grid sm:grid-cols-2 gap-6'}>
                        {relatedPosts.map((p) => (
                            <VerticalPost key={p.id} {...p} />
                        ))}
                    </ul>
                </div>
            </div>
        </>
    );
};

export default Page;

export const getStaticProps: GetStaticProps = async ({
    params
}: {
    params: { id: string };
}): Promise<{ props: { post: PostEntity; relatedPosts: PostEntity[] } }> => {
    const post = getPostData(params.id);
    const content = await md2Html(post.content);
    const summary = await md2Html(post.summary);
    const relatedPosts = getRelatedPosts(params.id);

    if (!post.posted) {
        throw new Error('Post not ready to be published!');
    }
    return {
        props: {
            post: {
                ...post,
                summary,
                content
            },
            relatedPosts
        }
    };
};

export const getStaticPaths = async (): Promise<{
    paths: { params: { id: string } }[];
    fallback: false;
}> => ({
    paths: getAllPostIds(),
    fallback: false
});
